import getSlug from './slug-service';

class PropertiesService {
  static instance = new PropertiesService();

  hostname = () => process.env.GATSBY_CONFLUENCE_HOSTNAME;

  jiraBase = () => `https://${this.hostname()}/servicedesk/customer/portal/15/group/51/create`;

  jiraJobApplication = (title) => this.jiraBase() + `/161?summary=${title}`;

  jiraRegister = () => this.jiraBase() + '/165';

  getProperties = (confluencePage) => {
    if (!confluencePage) return {};

    const bodyDynamic = confluencePage.node.bodyDynamic;

    if (!bodyDynamic) {
      return {};
    }

    const jsonDynamic = JSON.parse(bodyDynamic).content.filter(
      (contentItem) => contentItem.attrs && contentItem.attrs.extensionKey === 'details',
    )[0]?.content[0]?.content;

    if (!jsonDynamic) {
      return {};
    }

    let properties = {};

    jsonDynamic
      .filter((row) => row.type === 'tableRow')
      .forEach((row) => {
        const key = this.getKey(row);
        const value = this.getValue(row);
        properties[key] = value;
      });

    return properties;
  };

  getPropertyAsDate = (confluencePage, property) => {
    const date = this.getProperties(confluencePage)[property];

    if (!date) return undefined;

    const dateObject = new Date(+date.trim());

    return dateObject;
  };

  getPropertiesByPageTitle = (confluencePages, title) => {
    const dynamicResult = confluencePages.find((result) => result.title === title);

    if (!dynamicResult) {
      console.warn(`Unable to find page with title '${title}'`);
    }
    return this.getProperties(dynamicResult);
  };

  getAncestors = (confluencePage) => {
    return confluencePage.ancestors.map((ancestor) => ancestor.title);
  };

  getAvailable = (confluencePage) => {
    return confluencePage.filter(
      (edge) =>
        process.env.GATSBY_BUILD_ENVIRONMENT === 'develop' ||
        edge.node.labels.map((label) => label.name).includes('publish'),
    );
  };

  getKey = (row) => {
    const headerCell = row.content.filter((content) => content.type === 'tableHeader')[0];

    return headerCell.content[0].content[0].text;
  };

  getValue = (row) => {
    const tableCell = row.content.filter((content) => content.type === 'tableCell')[0].content;

    return tableCell
      .flatMap((content) => content.content)
      .map((content) => this.getContentValue(content))
      .join(' ');
  };

  getContentValue = (content) => {
    if (!content || !content.type) {
      return '';
    }

    if (content.type === 'text') {
      return content.text;
    }

    if (content.type === 'date') {
      const timestamp = content.attrs.timestamp;
      return timestamp;
    }

    if (content.type === 'listItem') {
      return content.content.flatMap((content) => content.content).map((content) => this.getContentValue(content));
    }
  };

  getLabels = (confluencePage) => {
    if (!confluencePage || !confluencePage.node || !confluencePage.node.labels) {
      return [];
    }
    return confluencePage.node.labels.map((result) => result.name);
  };

  getPagesWithLabels = (confluencePages, labels) => {
    let results = confluencePages;

    for (let i = 0; i < labels.length; i++) {
      results = confluencePages.filter((edge) => this.getLabels(edge).includes(labels[i]));
    }

    return results;
  };

  getPageByConfluenceId = (confluencePages, confluenceId) => {
    return confluencePages.find((result) => result.node.confluenceId === confluenceId);
  };

  getPageTitleById = (anchor, confluencePages) => {
    const id = anchor.replace(/.*wiki\/[^\d]*(\d+).*/g, '$1');
    return confluencePages.find((result) => result.node.confluenceId === id)?.node.title;
  };

  getAttachmentByLabel = (confluencePage, label) => {
    if (!confluencePage || !confluencePage.node) return undefined;

    return confluencePage.node.attachments.find((attachment) => {
      return attachment.metadata.labels.results.find((result) => result.label === label) != null;
    });
  };

  getAllAttachmentByLabel = (confluencePage, label) => {
    if (!confluencePage || !confluencePage.node) return undefined;

    return confluencePage.node.attachments.filter((attachment) => {
      return attachment.metadata.labels.results.find((result) => result.label === label) != null;
    });
  };

  getChildren = (confluencePages, confluencePage) => {
    if (!confluencePage || !confluencePage.node || !confluencePage.node.confluenceChildren) return [];

    const childIds = confluencePage.node.confluenceChildren;

    const results = confluencePages.filter((page) => childIds.includes(page.node.confluenceId));

    // filter to available then ensure we're sorted by the same order in the confluence ui
    return this.getAvailable(results).sort(
      (a, b) => childIds.indexOf(a.node.confluenceId) - childIds.indexOf(b.node.confluenceId),
    );
  };

  fixDownloads = (id, html) => {
    return html.replace(
      /href="\/wiki\/download\/attachments\/\d+\/([^?]*)[^>]*><img .*?src="https.*?wiki\/download\/thumbnails\/([^?]*)[^>]*>/g,
      (match, p1) => `href="/${id}-${p1}">${decodeURIComponent(p1)}`,
    );
  };

  fixAnchors = (html, confluencePages) => {
    if (!confluencePages) {
      return html;
    }

    return html.replace(
      /href="[^>]*?\/wiki\/[^\d]*\d+[^>]*"/g,
      (anchor) => 'href="/' + getSlug(this.getPageTitleById(anchor, confluencePages)) + '"',
    );
  };

  replaceImages = (id, bodyHtml, images) => {
    const replaceImage = (html, imageUrl) => {
      return html.replace(
        new RegExp('<img .*?src="https://' + this.hostname() + '/wiki/download/attachments/\\d+/([^?]*)[^>]*>', 'g'),
        `<img src='/${id}-$1'/>`,
      );
    };

    if (images) {
      images.forEach((url) => (bodyHtml = replaceImage(bodyHtml, url)));
    }

    return bodyHtml;
  };

  replaceThumbnails = (id, bodyHtml) => {
    return bodyHtml.replace(
      new RegExp('<img .*?src="https://' + this.hostname() + '/wiki/download/thumbnails/\\d+/([^?]*)[^>]*>', 'g'),
      `<img src='/${id}-$1'/>`,
    );
  };

  removeLinks = (bodyHtml) => {
    return bodyHtml
      .replace(new RegExp('<a [^>]*?href="https://' + this.hostname() + '/wiki[^>]*>([^<]*)</a>', 'g'), '$1')
      .replace(new RegExp('<a [^>]*?href="/">([^<]*)</a>', 'g'), '$1');
  };

  sortResults(labels) {
    return (a, b) => {
      if (labels.includes('sort-date')) {
        const p = 'Publication Date';
        return this.getPropertyAsDate(a, p) > this.getPropertyAsDate(b, p) ? -1 : 1;
      } else if (labels.includes('sort-position')) {
        return this.getProperties(a)['Menu Position'] > this.getProperties(b)['Menu Position'] ? 1 : -1;
      } else if (labels.includes('sort-title')) {
        return a.node.title > b.node.title ? 1 : -1;
      } else {
        return 0;
      }
    };
  }
}

export default PropertiesService.instance;
